import { useEffect, useState } from "react"
import { CircularProgress, Grid, Modal, ObjectiveCard, Typography } from "src/lib"
import { useOpportunitiesStore } from "src/store/opportunitiesStore"
import { OpportunitiesState } from "src/store/opportunitiesStore/types"
import { IRelatedOpportunitiesModalProps } from "./types"

const RelatedOpportunitiesModal = ({ client, open, ...props }: IRelatedOpportunitiesModalProps) => {
    const [loading, setLoading] = useState<boolean>(false)
    const [relatedOpportunities, setRelatedOpportunities] = useState<any[]>([])
    const opportunitiesData = useOpportunitiesStore((s: OpportunitiesState) => s.opportunitiesData)

    const formatAddedAt = (createdDate: string) => {
        const date = new Date(createdDate)
        if (Number.isNaN(date.getTime())) {
            return createdDate
        }
        return date.toLocaleDateString("pt-BR")
    }

    const formatDueDate = (objective: { endDate?: string; dueDate?: string }) => {
        const due = (objective.endDate ?? objective.dueDate)?.trim()
        if (due) {
            const parsed = new Date(`${due}T12:00:00`)
            if (!Number.isNaN(parsed.getTime())) {
                return parsed.toLocaleDateString("pt-BR")
            }
            return due
        }
        return ""
    }

    useEffect(() => {
        const searchRelatedOpportunities = async () => {
            if (!client || !open) {
                setRelatedOpportunities([])
                return
            }

            // Verifica se o client tem as propriedades necessárias
            if (!client.name || client.account === undefined) {
                console.warn("Client payload inválido:", client)
                setRelatedOpportunities([])
                return
            }

            try {
                setLoading(true)

                // Simula um delay para mostrar o loading durante a busca
                await new Promise(resolve => setTimeout(resolve, 500))

                // Busca as oportunidades relacionadas ao cliente no store
                if (opportunitiesData?.objectives) {
                    const filtered = opportunitiesData.objectives.filter((opportunity) => {
                        if (opportunity.isExpired) return false
                        return opportunity.impacted_clients?.some(
                            (impactedClient) =>
                                impactedClient?.p?.name === client.name &&
                                String(impactedClient?.p?.account ?? "") === String(client.account ?? "")
                        )
                    })
                    setRelatedOpportunities(filtered)
                } else {
                    setRelatedOpportunities([])
                }
            } catch (error) {
                console.error("Erro ao buscar oportunidades relacionadas:", error)
                setRelatedOpportunities([])
            } finally {
                setLoading(false)
            }
        }

        searchRelatedOpportunities()
    }, [client, open, opportunitiesData])

    if (!client) {
        return null
    }

    return (
        <Modal
            {...props}
            open={open}
            title="Oportunidades relacionadas ao cliente"
        >
            {loading ? (
                <Grid container justifyContent="center" sx={{ py: 4 }}>
                    <CircularProgress color="primary" />
                </Grid>
            ) : relatedOpportunities.length === 0 ? (
                <Typography variant="body2" color="quaternary">
                    Nenhuma oportunidade encontrada para este cliente.
                </Typography>
            ) : (
                <Grid
                    container
                    spacing={2}
                    sx={{ maxHeight: '60vh', overflowY: 'auto', alignContent: 'flex-start' }}
                    alignItems="stretch"
                >
                    {relatedOpportunities.map((objective) => (
                        <Grid item xs={12} sm={6} key={`${objective.title}  ${objective.createdDate}`}>
                            <ObjectiveCard
                                fullWidth
                                fullHeight
                                title={objective.title}
                                objectiveId={objective.objective_id}
                                recommendation={objective.text}
                                addedAt={formatAddedAt(objective.createdDate)}
                                dueDate={formatDueDate(objective)}
                                isExpired={objective.isExpired}
                                impactedClients={String(objective.impacted_clients?.length ?? 0)}
                                impactedClientsArray={objective.impacted_clients}
                                context={objective.title}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}
        </Modal>
    )
}

export { RelatedOpportunitiesModal }